// Project factory function following functional architecture patterns

import type {
  Project,
  ProjectAPI,
  ProjectConfig,
  ProjectMetadata,
  ProjectSettings,
  ProjectDesignSystem,
  ProjectTeam,
  ProjectType,
  TargetLanguage,
  Page,
  ProjectElement,
  SketchData,
} from "../types/project-legacy-types";
import { createPage } from "./page";
import { createDesignSystemForProjectType } from "./design-system";

// Default values
const DEFAULT_PROJECT_SETTINGS: ProjectSettings = {
  canvasWidth: 1200,
  canvasHeight: 800,
  deviceType: "desktop",
  theme: ["light"],
  grid: {
    enabled: true,
    spacing: 24,
    color: "#e0e0e0",
  },
  helpers: {
    snap: true,
    rulers: false,
    dots: false,
    lines: false,
  },
};

const createDefaultPage = (name: string = "Page 1"): Page => ({
  id: generateId(),
  name,
  order: 0,
  version: "v1",
  draft: false,
  type: "component",
  description: "",
  data: { sketches: {}, annotations: {}, properties: {}, children: {} },
  elements: [],
  sketches: [],
  created: new Date(),
  modified: new Date(),
});

const createDefaultTeam = (ownerId: string): ProjectTeam => ({
  ownerId,
  members: [
    {
      userId: ownerId,
      role: "owner",
      joinedAt: new Date(),
    },
  ],
});

const createDefaultDesignSystem = (
  projectType: ProjectType,
  ownerId: string
): ProjectDesignSystem => {
  const baseSystem = createDesignSystemForProjectType(
    projectType,
    "Project Design System",
    ownerId
  );

  return {
    baseSystemId: baseSystem.id,
    localOverrides: {},
    customProperties: {},
    context: projectType,
  };
};

const getDefaultPagesByProjectType = (projectType: ProjectType): Page[] => {
  const defaultPages: Record<
    ProjectType,
    { name: string; type: "home" | "slug" | "component" }[]
  > = {
    website: [
      { name: "Home", type: "home" },
      { name: "Contact", type: "slug" },
      { name: "404", type: "slug" },
      { name: "Error", type: "slug" },
    ],
    dashboard: [
      { name: "Dashboard", type: "component" },
      { name: "Login", type: "slug" },
      { name: "Settings", type: "component" },
    ],
    app: [
      { name: "Splash", type: "component" },
      { name: "Onboarding", type: "component" },
      { name: "Main", type: "component" },
      { name: "Profile", type: "component" },
    ],
  };

  return defaultPages[projectType].map((config, index) => {
    const domain = createPage({ name: config.name, type: config.type });
    const page: Page = {
      id: domain.id,
      name: domain.name,
      order: index,
      version: domain.version,
      draft: domain.draft,
      type: domain.type as any,
      description: domain.description ?? "",
      data: {
        sketches: domain.data.sketches,
        annotations: domain.data.annotations,
        properties: domain.data.properties,
        children: domain.data.children,
      },
      elements: [],
      sketches: [],
      created: new Date(),
      modified: new Date(),
    };
    return page;
  });
};

// Helper to generate unique IDs
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * Creates a project instance with full CRUD operations
 *
 * @param config - Configuration for project initialization
 * @returns Project API with state management methods
 *
 * @example
 * ```typescript
 * const project = createProject({
 *   initialProject: { metadata: { name: "My App" } },
 *   autoSave: true,
 *   onProjectChange: (proj) => console.log('Project updated:', proj)
 * });
 *
 * const pageId = project.addPage("Login Screen");
 * project.setCurrentPage(pageId);
 * project.destroy(); // Always call when done
 * ```
 */
export const createProject = (config: ProjectConfig = {}): ProjectAPI => {
  const {
    initialProject = {},
    autoSave = false,
    storageKey = "sketch-project",
    onProjectChange,
    onPageChange,
    onElementChange,
  } = config;

  // Initialize project with defaults
  const defaultMetadata: ProjectMetadata = {
    id: generateId(),
    name: "Untitled Project",
    description: "",
    projectType: "website" as ProjectType,
    appLanguages: ["en"],
    targetLanguage: "ES6" as TargetLanguage,
    isPasswordProtected: false,
    created: new Date(),
    modified: new Date(),
    version: "1.0.0",
    tags: [],
  };

  const metadata = {
    ...defaultMetadata,
    ...initialProject.metadata,
  };

  const defaultPages = getDefaultPagesByProjectType(metadata.projectType);
  const pages = initialProject.pages || defaultPages;
  const currentPageId = initialProject.currentPageId || pages[0]?.id || "";

  // Create team with current user as owner (for now using a placeholder)
  const ownerId = "current-user"; // This should come from auth context
  const defaultTeam = createDefaultTeam(ownerId);
  const team: ProjectTeam = {
    ownerId: initialProject.team?.ownerId ?? defaultTeam.ownerId,
    members: initialProject.team?.members ?? defaultTeam.members,
  };

  // Create design system based on project type and merge partial overrides
  const defaultDesignSystem = createDefaultDesignSystem(
    metadata.projectType,
    ownerId
  );
  const designSystem: ProjectDesignSystem = {
    ...defaultDesignSystem,
    ...(initialProject.designSystem || {}),
    localOverrides: {
      ...defaultDesignSystem.localOverrides,
      ...(initialProject.designSystem?.localOverrides || {}),
    },
    customProperties: {
      ...defaultDesignSystem.customProperties,
      ...(initialProject.designSystem?.customProperties || {}),
    },
  };

  let project: Project = {
    metadata,
    settings: {
      ...DEFAULT_PROJECT_SETTINGS,
      ...initialProject.settings,
    },
    designSystem,
    team,
    pages,
    appLanguages: metadata.appLanguages,
    currentPageId,
  };

  // Load from localStorage if available
  if (autoSave && typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        project = {
          ...project,
          ...parsed,
          metadata: {
            ...project.metadata,
            ...parsed.metadata,
          },
          settings: {
            ...project.settings,
            ...parsed.settings,
          },
          designSystem: {
            ...project.designSystem,
            ...parsed.designSystem,
          },
          team: {
            ...project.team,
            ...parsed.team,
          },
        };
      }
    } catch (error) {
      console.warn("Failed to load project from storage:", error);
    }
  }

  // Helper to trigger updates and persistence
  const notifyChange = () => {
    project = {
      ...project,
      metadata: {
        ...project.metadata,
        modified: new Date(),
      },
    };

    onProjectChange?.(project);

    if (autoSave && typeof window !== "undefined") {
      try {
        localStorage.setItem(storageKey, JSON.stringify(project));
      } catch (error) {
        console.warn("Failed to save project to storage:", error);
      }
    }
  };

  // Public API
  const getProject = (): Project => ({
    metadata: { ...project.metadata },
    settings: { ...project.settings },
    designSystem: { ...project.designSystem },
    team: { ...project.team },
    appLanguages: project.metadata.appLanguages,
    pages: project.pages.map((page) => ({
      ...page,
      elements: [...page.elements],
      sketches: [...page.sketches],
    })),
    currentPageId: project.currentPageId,
  });

  const updateMetadata = (updates: Partial<ProjectMetadata>) => {
    project = {
      ...project,
      metadata: {
        ...project.metadata,
        ...updates,
      },
    };
    notifyChange();
  };

  const updateSettings = (updates: Partial<ProjectSettings>) => {
    project = {
      ...project,
      settings: {
        ...project.settings,
        ...updates,
        grid: {
          ...project.settings.grid,
          ...updates.grid,
        },
        helpers: {
          ...project.settings.helpers,
          ...updates.helpers,
        },
      },
    };
    notifyChange();
  };

  const updateDesignSystem = (updates: Partial<ProjectDesignSystem>) => {
    project = {
      ...project,
      designSystem: {
        ...project.designSystem,
        ...updates,
        localOverrides: {
          ...project.designSystem.localOverrides,
          ...updates.localOverrides,
        },
        customProperties: {
          ...project.designSystem.customProperties,
          ...updates.customProperties,
        },
      },
    };
    notifyChange();
  };

  const updateTeam = (updates: Partial<ProjectTeam>) => {
    project = {
      ...project,
      team: {
        ...project.team,
        ...updates,
      },
    };
    notifyChange();
  };

  const addTeamMember = (
    userId: string,
    role: ProjectTeam["members"][0]["role"]
  ) => {
    const existingMember = project.team.members.find(
      (m) => m.userId === userId
    );
    if (existingMember) {
      console.warn(`User ${userId} is already a team member`);
      return;
    }

    project = {
      ...project,
      team: {
        ...project.team,
        members: [
          ...project.team.members,
          {
            userId,
            role,
            joinedAt: new Date(),
          },
        ],
      },
    };
    notifyChange();
  };

  const removeTeamMember = (userId: string) => {
    if (project.team.ownerId === userId) {
      console.warn("Cannot remove project owner from team");
      return;
    }

    project = {
      ...project,
      team: {
        ...project.team,
        members: project.team.members.filter((m) => m.userId !== userId),
      },
    };
    notifyChange();
  };

  const updateTeamMemberRole = (
    userId: string,
    role: ProjectTeam["members"][0]["role"]
  ) => {
    if (project.team.ownerId === userId && role !== "owner") {
      console.warn("Cannot change owner role");
      return;
    }

    project = {
      ...project,
      team: {
        ...project.team,
        members: project.team.members.map((member) =>
          member.userId === userId ? { ...member, role } : member
        ),
      },
    };
    notifyChange();
  };

  const addPage = (name: string): string => {
    const newPage: Page = {
      id: generateId(),
      name,
      order: project.pages.length,
      version: "v1",
      draft: false,
      type: "component",
      description: "",
      data: { sketches: {}, annotations: {}, properties: {}, children: {} },
      elements: [],
      sketches: [],
      created: new Date(),
      modified: new Date(),
    };

    project = {
      ...project,
      pages: [...project.pages, newPage],
    };

    notifyChange();
    return newPage.id;
  };

  const removePage = (pageId: string) => {
    if (project.pages.length <= 1) {
      console.warn("Cannot remove the last page");
      return;
    }

    const pageIndex = project.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;

    const newPages = project.pages.filter((p) => p.id !== pageId);

    // If removing current page, switch to first available page
    let newCurrentPageId = project.currentPageId;
    if (project.currentPageId === pageId) {
      newCurrentPageId = newPages[0]?.id || "";
    }

    project = {
      ...project,
      pages: newPages as readonly Page[],
      currentPageId: newCurrentPageId,
    };

    notifyChange();
    if (newCurrentPageId !== project.currentPageId) {
      onPageChange?.(newCurrentPageId);
    }
  };

  const renamePage = (pageId: string, name: string) => {
    const pageIndex = project.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;

    const currentPage = project.pages[pageIndex];
    if (!currentPage) return;

    const updatedPage: Page = {
      ...currentPage,
      name,
      modified: new Date(),
    };

    project = {
      ...project,
      pages: project.pages.map((page, index) =>
        index === pageIndex ? updatedPage : page
      ) as readonly Page[],
    };

    notifyChange();
  };

  const setCurrentPage = (pageId: string) => {
    if (!project.pages.some((p) => p.id === pageId)) {
      console.warn(`Page ${pageId} not found`);
      return;
    }

    project = {
      ...project,
      currentPageId: pageId,
    };

    onPageChange?.(pageId);
    notifyChange();
  };

  const addElement = (
    pageId: string,
    element: Omit<ProjectElement, "id" | "created" | "modified">
  ): string => {
    const pageIndex = project.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return "";

    const currentPage = project.pages[pageIndex];
    if (!currentPage) return "";

    const newElement: ProjectElement = {
      ...element,
      id: generateId(),
      created: new Date(),
      modified: new Date(),
    };

    const updatedPage: Page = {
      ...currentPage,
      elements: [...currentPage.elements, newElement],
      modified: new Date(),
    };

    project = {
      ...project,
      pages: project.pages.map((page, index) =>
        index === pageIndex ? updatedPage : page
      ) as readonly Page[],
    };

    notifyChange();
    onElementChange?.(pageId, newElement.id);
    return newElement.id;
  };

  const updateElement = (
    pageId: string,
    elementId: string,
    updates: Partial<ProjectElement>
  ) => {
    const pageIndex = project.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;

    const currentPage = project.pages[pageIndex];
    if (!currentPage) return;

    const elementIndex = currentPage.elements.findIndex(
      (e) => e.id === elementId
    );
    if (elementIndex === -1) return;

    const currentElement = currentPage.elements[elementIndex];
    if (!currentElement) return;

    const updatedElement: ProjectElement = {
      ...currentElement,
      ...updates,
      modified: new Date(),
    };

    const updatedPage: Page = {
      ...currentPage,
      elements: currentPage.elements.map((element, index) =>
        index === elementIndex ? updatedElement : element
      ),
      modified: new Date(),
    };

    project = {
      ...project,
      pages: project.pages.map((page, index) =>
        index === pageIndex ? updatedPage : page
      ) as readonly Page[],
    };

    notifyChange();
    onElementChange?.(pageId, elementId);
  };

  const removeElement = (pageId: string, elementId: string) => {
    const pageIndex = project.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;

    const currentPage = project.pages[pageIndex];
    if (!currentPage) return;

    const updatedPage: Page = {
      ...currentPage,
      elements: currentPage.elements.filter((e) => e.id !== elementId),
      modified: new Date(),
    };

    project = {
      ...project,
      pages: project.pages.map((page, index) =>
        index === pageIndex ? updatedPage : page
      ) as readonly Page[],
    };

    notifyChange();
  };

  const addSketch = (
    pageId: string,
    sketch: Omit<SketchData, "id" | "created">
  ): string => {
    const pageIndex = project.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return "";

    const currentPage = project.pages[pageIndex];
    if (!currentPage) return "";

    const newSketch: SketchData = {
      ...sketch,
      id: generateId(),
      created: new Date(),
    };

    const updatedPage: Page = {
      ...currentPage,
      sketches: [...currentPage.sketches, newSketch],
      modified: new Date(),
    };

    project = {
      ...project,
      pages: project.pages.map((page, index) =>
        index === pageIndex ? updatedPage : page
      ) as readonly Page[],
    };

    notifyChange();
    return newSketch.id;
  };

  const serialize = (): string => {
    return JSON.stringify(project);
  };

  const deserialize = (data: string) => {
    try {
      const parsed = JSON.parse(data);
      project = {
        metadata: {
          ...defaultMetadata,
          ...parsed.metadata,
        },
        settings: {
          ...DEFAULT_PROJECT_SETTINGS,
          ...parsed.settings,
        },
        designSystem:
          parsed.designSystem ||
          createDefaultDesignSystem("website", "current-user"),
        team: parsed.team || createDefaultTeam("current-user"),
        appLanguages: parsed.appLanguages || ["en"],
        pages: parsed.pages?.map((p: any, index: number) => ({
          id: p.id ?? generateId(),
          name: p.name ?? `Page ${index + 1}`,
          order: p.order ?? index,
          version: p.version ?? "v1",
          draft: p.draft ?? false,
          type: (p.type ?? "component") as any,
          description: p.description ?? "",
          data: {
            sketches: p.data?.sketches ?? {},
            annotations: p.data?.annotations ?? {},
            properties: p.data?.properties ?? {},
            children: p.data?.children ?? {},
          },
          elements: p.elements ?? [],
          sketches: p.sketches ?? [],
          created: p.created ? new Date(p.created) : new Date(),
          modified: p.modified ? new Date(p.modified) : new Date(),
        })) || [createDefaultPage()],
        currentPageId: parsed.currentPageId || parsed.pages?.[0]?.id || "",
      };
      notifyChange();
    } catch (error) {
      console.error("Failed to deserialize project:", error);
    }
  };

  const destroy = () => {
    // Cleanup any resources if needed
    // Currently no cleanup required for this implementation
  };

  return {
    getProject,
    updateMetadata,
    updateSettings,
    updateDesignSystem,
    updateTeam,
    addTeamMember,
    removeTeamMember,
    updateTeamMemberRole,
    addPage,
    removePage,
    renamePage,
    setCurrentPage,
    addElement,
    updateElement,
    removeElement,
    addSketch,
    serialize,
    deserialize,
    destroy,
  };
};
